import React, { useContext } from 'react';
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';
import { renderRichText } from 'gatsby-source-contentful/rich-text';
import { navigate } from 'gatsby';
import { Label } from '@nooz-optics/nooz-components-lib';
import { returnLocalisedPath } from '../../../../../utils/globals';
import { IBodyProps } from '../../Body/body';
import { useMediaQuery } from '../../../../../utils/hooks/useMediaQuery';
import { Image } from '../../../Media/Image/index';
import { LocalContext } from '../../../../../utils/providers/locale';
import { Link } from '../../Link/index';
import { Component } from '../../../../Contentful/component';
import { Grid } from '../../../Grid/grid';
import { Iframe } from '../../../Media';
import { getContentType, nodes } from '../body.utils';
import { Button } from '../..';

const BlogImage = ({ node, currentKey }) => {
  let imageUrl = null;

  if (node.data?.target) {
    const { file, urls, description } = node.data?.target.media;

    if (file?.url) {
      imageUrl = file?.url;
    } else {
      const imageUrls = urls;
      imageUrl = imageUrls[0].file.url;

      const isWindowMediumSize: boolean = useMediaQuery('sm');

      if (!isWindowMediumSize && imageUrls[1]) {
        imageUrl = imageUrls[1].file.url;
      }
    }

    return (
      <div
        className='flex flex-col justify-center w-full my-16 overflow-hidden'
        style={{ maxHeight: 600 }}>
        <Image
          objectFit='cover'
          media={{
            file: { url: imageUrl },
          }}
        />
        {description && description[currentKey] && (
          <div className='w-full items-start'>
            <BodyLabel
              text={description[currentKey]}
              size='sm'
            />
          </div>
        )}
      </div>
    );
  }
  return '';
};

const BodyLabel = (props) => <Label {...props} />;

export const options = (locale, key) => ({
  renderMark: {
    [MARKS.BOLD]: (text) => <strong className='text-medium'>{text}</strong>,
  },
  renderNode: {
    ...nodes,
    [BLOCKS.HEADING_1]: (node, children) => (
      <div className='my-4 '>
        <BodyLabel
          type='h1'
          color={{ classname: 'gray-800' }}
          size='7xl'
          text={children}
        />
      </div>
    ),
    [BLOCKS.HEADING_2]: (node, children) => (
      <div className='my-4 '>
        <BodyLabel
          type='h2'
          color={{ classname: 'gray-800' }}
          size='4xl'
          text={children}
        />
      </div>
    ),
    [BLOCKS.HEADING_3]: (node, children) => (
      <div className='my-4 '>
        <BodyLabel
          type='h3'
          color={{ classname: 'gray-800' }}
          size='2xl'
          text={children}
        />
      </div>
    ),
    [BLOCKS.HEADING_4]: (node, children) => (
      <BodyLabel
        type='h4'
        color={{ classname: 'gray-800' }}
        size='xl'
        text={children}
      />
    ),
    [BLOCKS.HEADING_5]: (node, children) => (
      <BodyLabel
        type='h5'
        color={{ classname: 'gray-800' }}
        size=' xl'
        text={children}
      />
    ),
    [BLOCKS.HEADING_6]: (node, children) => (
      <BodyLabel
        type='h6'
        color={{ classname: 'gray-900' }}
        size='lg'
        text={children}
      />
    ),
    [BLOCKS.PARAGRAPH]: (node, children) => {
      // Handle line breaks
      if (children?.filter((e) => !!e).length === 0) {
        return <br />;
      }

      return (
        <BodyLabel
          family='font-inter'
          weight='light'
          type='p'
          size='lg'
          text={children}
        />
      );
    },

    [BLOCKS.UL_LIST]: ({ content }) => (
      <ul className='list-disc my-2'>
        {content?.map((cont) =>
          cont.content.map((c) => (
            <li className='ml-5'>
              <BodyLabel
                family='font-inter'
                type='p'
                size='lg'
                text={c.content[0].value}
              />
            </li>
          ))
        )}
      </ul>
    ),

    [BLOCKS.HR]: () => <div className='w-1/5 border-2' />,

    [BLOCKS.EMBEDDED_ENTRY]: (node) => {
      const contentType = getContentType(node);

      if (contentType === 'ContentfulComponent') {
        if (node.data.target.path) {
          return <Component {...node.data.target} />;
        }

        if (
          node?.data?.target?.items &&
          node?.data?.target?.items[0]?.internal?.type === 'ContentfulImage'
        ) {
          return (
            <BlogImage
              node={{
                data: { target: node?.data?.target?.items[0] },
              }}
              currentKey={key}
            />
          );
        }

        if (
          node?.data?.target?.items &&
          node?.data?.target?.items[0]?.internal?.type === 'ContentfulButton'
        ) {
          return (
            <div className='flex justify-center my-6'>
              <Button
                {...node?.data?.target?.items[0]}
                size='lg'
                type='white'
              />
            </div>
          );
        }
      }
      if (contentType === 'ContentfulLink') {
        return <Link {...node.data.target} />;
      }

      if (contentType === 'ContentfulGrid') {
        return <Grid grid={node.data.target} />;
      }

      if (contentType === 'Entry' && node?.data?.target?.key === 'iframe') {
        return (
          <Iframe
            title={node.data?.target?.title}
            mediaUrl={node?.data?.target?.mediaUrl}
          />
        );
      }
      if (contentType === 'ContentfulButton') {
        return (
          <div className='flex justify-center my-6'>
            <Button
              size='lg'
              type='white'
              label={{ text: node.data.target.label?.text }}
              onClick={() =>
                navigate(
                  returnLocalisedPath(locale, node.data.target?.slug?.url)
                )
              }
            />
          </div>
        );
      }

      if (contentType === 'ContentfulImage') {
        return (
          <BlogImage
            node={node}
            currentKey={key}
          />
        );
      }
    },
    [BLOCKS.EMBEDDED_ASSET]: (node) =>
      node.data.target?.file && (
        <BlogImage
          node={node}
          currentKey={key}
        />
      ),
    [INLINES.EMBEDDED_ENTRY]: (node) => {
      const contentType = getContentType(node);

      if (contentType === 'ContentfulImage') {
        return (
          <BlogImage
            node={node}
            currentKey={key}
          />
        );
      }
      if (contentType === 'ContentfulLink') {
        return <Link {...node.data.target} />;
      }
    },
    [INLINES.HYPERLINK]: (node, children) => {
      if (node.data.uri.includes('http')) {
        return (
          <span className='underline hover:text-blue-500'>
            <Link
              type='URL'
              urlLink={node.data.uri}
              label={{ type: 'span', text: children }}
            />
          </span>
        );
      }
      // return (
      //   <span className="underline hover:text-blue-500">
      //     <Link type="Page" label={children} slug={{ url: node.data.uri }} />
      //   </span>
      // );
    },
  },
});

export const BlogBody = ({ richBody }: IBodyProps) => {
  const locale = useContext(LocalContext)[0];
  const contentfulLocal = () => {
    const arr = locale.toLowerCase().split('-');
    return `${arr[0]}${arr[1] && `-${arr[1].toUpperCase()}`}`;
  };

  const key = contentfulLocal().includes('en') ? 'en' : contentfulLocal();
  return richBody ? (
    <>{renderRichText(richBody, options(locale, key))}</>
  ) : (
    <></>
  );
};
